import React, { useState, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { getApiUrl, addUserParam } from '../config/api.config';
import {
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemSecondaryAction,
  Select,
  FormControl,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { saveList, deleteList, fetchUserLists } from '../store/listsSlice';
import AlertsManager from './AlertsManager';

const BurgerMenu = ({ selectedLists, onListsChange }) => {
  const dispatch = useDispatch();
  const { lists } = useSelector((state) => state.lists);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Dialog states
  const [uploadDialog, setUploadDialog] = useState(false);
  const [manageDialog, setManageDialog] = useState(false);
  const [alertsDialog, setAlertsDialog] = useState(false);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadListName, setUploadListName] = useState('');
  
  // List management state
  const [editingList, setEditingList] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [tickerInput, setTickerInput] = useState('');
  const [selectedTickers, setSelectedTickers] = useState([]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadFile = () => {
    setUploadDialog(true);
    handleMenuClose();
  };

  const handleManageLists = () => {
    setManageDialog(true);
    handleMenuClose();
  };

  const handleOpenAlerts = () => {
    setAlertsDialog(true);
    handleMenuClose();
  };

  const handleEditList = (listName) => {
    setEditingList(listName);
    setNewListName(listName);
    setSelectedTickers([...lists[listName]]);
    setTickerInput('');
  };

  const handleDeleteList = async (listName) => {
    if (window.confirm(`Are you sure you want to delete the list "${listName}"? This cannot be undone.`)) {
      try {
        await dispatch(deleteList(listName)).unwrap();
        toast.success(`List "${listName}" deleted`);

        // If we deleted the currently selected list, remove from selection
        if (selectedLists.includes(listName)) {
          const newSelection = selectedLists.filter(name => name !== listName);
          onListsChange(newSelection);
        }

        // If we were editing this list, close editor
        if (editingList === listName) {
          setEditingList(null);
        }
      } catch (error) {
        toast.error('Failed to delete list');
      }
    }
  };

  const handleRenameList = async () => {
    if (!newListName.trim() || newListName === editingList) {
      return;
    }

    try {
      // Save with new name
      await dispatch(saveList({ 
        name: newListName, 
        tickers: lists[editingList] 
      })).unwrap();
      
      // Delete old list
      await dispatch(deleteList(editingList)).unwrap();
      
      // Update selected lists if renamed list was selected
      if (selectedLists.includes(editingList)) {
        const newSelection = selectedLists.map(name => 
          name === editingList ? newListName : name
        );
        onListsChange(newSelection);
      }
      
      toast.success(`List renamed to "${newListName}"`);
      setEditingList(null);
    } catch (error) {
      toast.error('Failed to rename list');
    }
  };

  const handleAddTicker = () => {
    const ticker = tickerInput.trim().toUpperCase();
    if (ticker && !selectedTickers.includes(ticker)) {
      setSelectedTickers([...selectedTickers, ticker]);
      setTickerInput('');
    }
  };

  const handleRemoveTicker = (ticker) => {
    setSelectedTickers(selectedTickers.filter(t => t !== ticker));
  };

  const handleSaveTickerChanges = async () => {
    try {
      await dispatch(saveList({
        name: editingList,
        tickers: selectedTickers
      })).unwrap();
      
      toast.success(`List "${editingList}" updated`);
      setEditingList(null);
    } catch (error) {
      toast.error('Failed to update list');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/plain') {
      setUploadFile(file);
    } else {
      toast.error('Please select a .txt file');
    }
  };

  const processUploadFile = async () => {
    if (!uploadFile || !uploadListName.trim()) {
      toast.error('Please provide a file and list name');
      return;
    }

    try {
      const text = await uploadFile.text();
      const tickers = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      if (tickers.length === 0) {
        toast.error('No valid tickers found in file');
        return;
      }

      // Save the list
      await dispatch(saveList({
        name: uploadListName,
        tickers
      })).unwrap();

      toast.success(`List "${uploadListName}" created with ${tickers.length} tickers`);
      setUploadDialog(false);
      setUploadFile(null);
      setUploadListName('');
    } catch (error) {
      toast.error('Failed to process file: ' + error.message);
    }
  };

  const handleInitializeDefaultList = async () => {
    try {
      handleMenuClose();
      toast.loading('Loading default tickers...');

      const response = await fetch(addUserParam(`${getApiUrl()}/lists/initialize`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to initialize default list');
      }

      const result = await response.json();
      toast.dismiss();

      if (result.message && result.message.includes('already has lists')) {
        toast.success('You already have lists');
      } else {
        toast.success(`Created "${result.listName}" with ${result.tickerCount} tickers`);
        // Refresh lists
        dispatch(fetchUserLists());
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to load default list');
      console.error('Initialize default list error:', error);
    }
  };

  return (
    <>
      {/* Burger Menu Button */}
      <IconButton
        onClick={handleMenuOpen}
        color="primary"
        sx={{ 
          border: 1, 
          borderColor: 'primary.main',
          '&:hover': {
            backgroundColor: 'primary.main',
            color: 'primary.contrastText'
          }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleUploadFile}>
          <ListItemIcon>
            <UploadIcon />
          </ListItemIcon>
          <ListItemText primary="Upload Ticker List" />
        </MenuItem>
        
        {Object.keys(lists).length === 0 && (
          <MenuItem onClick={handleInitializeDefaultList}>
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="Load Default Tickers" />
          </MenuItem>
        )}
        
        <Divider />

        <MenuItem onClick={handleManageLists}>
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Manage Lists" />
        </MenuItem>

        <MenuItem onClick={handleOpenAlerts}>
          <ListItemIcon>
            <NotificationsIcon />
          </ListItemIcon>
          <ListItemText primary="Spread Alerts" />
        </MenuItem>
      </Menu>

      <AlertsManager mode="manage" open={alertsDialog} onClose={() => setAlertsDialog(false)} />

      {/* Upload File Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: { enter: 150, exit: 0 },
          unmountOnExit: true
        }}
      >
        <DialogTitle>Upload Ticker List</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a .txt file with ticker symbols (one per line)
            </Typography>
            
            <TextField
              label="List Name"
              fullWidth
              value={uploadListName}
              onChange={(e) => setUploadListName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="e.g., Banking Stocks, Tech Companies"
            />
            
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 2 }}
            >
              Choose File
              <input
                type="file"
                accept=".txt"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            
            {uploadFile && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Selected: {uploadFile.name}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button 
            onClick={processUploadFile} 
            variant="contained"
            disabled={!uploadFile || !uploadListName.trim()}
          >
            Upload & Create List
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Lists Dialog */}
      <Dialog
        open={manageDialog}
        onClose={() => setManageDialog(false)}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          timeout: { enter: 150, exit: 0 },
          unmountOnExit: true
        }}
      >
        <DialogTitle>
          {editingList ? `Edit List: ${editingList}` : 'Manage Lists'}
        </DialogTitle>
        <DialogContent>
          {!editingList ? (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on a list to rename it or manage its tickers
              </Typography>
              
              <List>
                {Object.keys(lists).map((listName) => (
                  <ListItem
                    key={listName}
                    onClick={() => handleEditList(listName)}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemText
                      primary={listName}
                      secondary={`${lists[listName]?.length || 0} tickers`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditList(listName);
                        }}
                        title="Edit list"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteList(listName);
                        }}
                        title="Delete list"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
              
              {Object.keys(lists).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No lists available. Create a list by uploading a ticker file.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {/* Rename List */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Rename List
                </Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    size="small"
                    fullWidth
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    placeholder="New list name"
                  />
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={handleRenameList}
                    disabled={!newListName.trim() || newListName === editingList}
                  >
                    Rename
                  </Button>
                </Box>
              </Box>
              
              {/* Add/Remove Tickers */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add Ticker
                </Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    size="small"
                    fullWidth
                    value={tickerInput}
                    onChange={(e) => setTickerInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTicker();
                      }
                    }}
                    placeholder="Enter ticker symbol"
                  />
                  <Button 
                    variant="contained" 
                    size="small"
                    onClick={handleAddTicker}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
              
              {/* Current Tickers */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Current Tickers ({selectedTickers.length})
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value=""
                    displayEmpty
                    renderValue={() => `${selectedTickers.length} tickers - Click to manage`}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem disabled value="">
                      <Typography variant="body2" color="text.secondary">
                        Click on ticker to remove
                      </Typography>
                    </MenuItem>
                    <Divider />
                    {selectedTickers.map((ticker) => (
                      <MenuItem key={ticker} value={ticker} sx={{ justifyContent: 'space-between' }}>
                        <Typography>{ticker}</Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTicker(ticker);
                          }}
                          sx={{ ml: 2 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editingList ? (
            <>
              <Button onClick={() => setEditingList(null)}>Back</Button>
              <Button 
                onClick={handleSaveTickerChanges} 
                variant="contained"
                color="primary"
              >
                Save Changes
              </Button>
            </>
          ) : (
            <Button onClick={() => setManageDialog(false)} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

// Memoize to prevent re-renders when parent state changes
export default memo(BurgerMenu);